import { SlotsFilters } from './filters/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const getFiltersFromSearch = (search: string): SlotsFilters => {
	const searchParams = new URLSearchParams(search);

	return {
		codeFilter: searchParams.get('code') ?? '',
		teacherFilter: searchParams.get('teacher') ?? '',
		dayFilter: searchParams.get('day') ?? '',
		durationFilter: searchParams.get('duration') ?? '',
		locationFilter: searchParams.get('location') ?? '',
		nonEmptyOnly: searchParams.get('nonEmpty') === '1',
		availableOnly: searchParams.get('available') === '1',
		startedOnly: searchParams.get('started') === '1',
	};
};

export const areFiltersEqual = (a: SlotsFilters, b: SlotsFilters) => {
	return (
		a.codeFilter === b.codeFilter &&
		a.teacherFilter === b.teacherFilter &&
		a.dayFilter === b.dayFilter &&
		a.durationFilter === b.durationFilter &&
		a.locationFilter === b.locationFilter &&
		a.nonEmptyOnly === b.nonEmptyOnly &&
		a.availableOnly === b.availableOnly &&
		a.startedOnly === b.startedOnly
	);
};

const setSingleFilterParam = (
	searchParams: URLSearchParams,
	key: 'code' | 'teacher' | 'day' | 'duration' | 'location',
	value: string
) => {
	if (value) searchParams.set(key, value);
	else searchParams.delete(key);
};

const setBooleanFilterParam = (
	searchParams: URLSearchParams,
	key: 'nonEmpty' | 'available' | 'started',
	value: boolean
) => {
	if (value) searchParams.set(key, '1');
	else searchParams.delete(key);
};

export function useSlotsFiltersUrlState() {
	const location = useLocation();
	const navigate = useNavigate();
	const skipNextUrlWriteRef = useRef(false);
	const [filters, setFilters] = useState<SlotsFilters>(() =>
		getFiltersFromSearch(location.search)
	);

	const patchFilters = useCallback((nextPatch: Partial<SlotsFilters>) => {
		setFilters((prev) => ({ ...prev, ...nextPatch }));
	}, []);

	useEffect(() => {
		const nextFilters = getFiltersFromSearch(location.search);

		setFilters((prev) => {
			if (areFiltersEqual(prev, nextFilters)) return prev;
			skipNextUrlWriteRef.current = true;
			return nextFilters;
		});
	}, [location.search]);

	useEffect(() => {
		if (skipNextUrlWriteRef.current) {
			skipNextUrlWriteRef.current = false;
			return;
		}

		const searchParams = new URLSearchParams(location.search);

		setSingleFilterParam(searchParams, 'code', filters.codeFilter);
		setSingleFilterParam(searchParams, 'teacher', filters.teacherFilter);
		setSingleFilterParam(searchParams, 'day', filters.dayFilter);
		setSingleFilterParam(searchParams, 'duration', filters.durationFilter);
		setSingleFilterParam(searchParams, 'location', filters.locationFilter);
		setBooleanFilterParam(searchParams, 'nonEmpty', filters.nonEmptyOnly);
		setBooleanFilterParam(searchParams, 'available', filters.availableOnly);
		setBooleanFilterParam(searchParams, 'started', filters.startedOnly);

		const nextSearch = searchParams.toString();
		const currentSearch = location.search.startsWith('?')
			? location.search.slice(1)
			: location.search;

		if (nextSearch === currentSearch) return;

		navigate(
			{
				pathname: location.pathname,
				search: nextSearch ? `?${nextSearch}` : '',
			},
			{ replace: true }
		);
	}, [filters, location.pathname, location.search, navigate]);

	return {
		filters,
		patchFilters,
	};
}